import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps a route to enforce billing access:
 *  - Grandfathered → full access
 *  - Active or in grace (≤5 days past_due) → full access (with banner via GraceBanner)
 *  - No subscription, not grandfathered → redirect to /welcome
 *  - Restricted (overdue >5 days) → redirect to /profile
 *
 * Use AFTER ProtectedRoute (assumes user is approved).
 */
export function SubscriptionGate({ children }: Props) {
  const sub = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (sub.status !== "ready") return;
    if (sub.hasAccess) return;

    // No active sub, no grace, not grandfathered.
    // If they have no subscription record at all → welcome screen
    // If their subscription is overdue/canceled and grace expired → restricted (profile only)
    const hasAnySub = !!sub.subscription;
    if (!hasAnySub) {
      if (location.pathname !== "/welcome") navigate("/welcome", { replace: true });
    } else {
      if (location.pathname !== "/profile") navigate("/profile#billing", { replace: true });
    }
  }, [sub, navigate, location.pathname]);

  if (sub.status !== "ready") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Chargement…</p>
      </div>
    );
  }

  if (!sub.hasAccess) return null;
  return <>{children}</>;
}
